import React, { useState, useEffect, memo } from 'react';
import PhotoModalWithAgent from './PhotoModalWithAgent';
import AgentContactModal from './AgentContactModal';
import { StyleConstants } from '../StyleConstants';
import analyticsService from '../services/analyticsService';
import { getGalleryImageUrl } from '../utils/imageUtils';
import { getAddressParts } from '../utils/addressUtils';

/**
 * ListingCard component for displaying active listings in a consistent card format
 * Similar to OpenHouseCard but without open house time badge
 */
const ListingCard = memo(({ listing, isMobile, onOpenModal, priority = false, loadingDelay = 0 }) => {
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isSelectingText, setIsSelectingText] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedAgentKey, setSelectedAgentKey] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Optimized image loading with priority and staggered loading
  useEffect(() => {
    if (!listing?.photo) return;

    const loadImage = () => {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageLoaded(true); // Still mark as loaded to show fallback
      // Preload the SAME resized URL we render to actually warm the browser cache.
      img.src = getGalleryImageUrl(listing.photo);
    };

    if (priority) {
      // High priority: load immediately
      loadImage();
    } else {
      // Staggered loading: delay based on position
      const timer = setTimeout(loadImage, loadingDelay);
      return () => clearTimeout(timer);
    }
  }, [listing?.photo, priority, loadingDelay]);

  // Calculate the count of photos
  const photoCount = (listing?.photos && Array.isArray(listing.photos))
    ? listing.photos.length
    : 0;

  // Extract essential listing details with fallbacks
  const {
    listingKey,
    listingId,
    address,
    price,
    beds,
    baths,
    sqft,
    city,
    photo,
    agentName,
    subdivisionName,
    daysOnMarket,
    status,
    propertyType,
    propertySubType,
    lotSizeAcres,
    listAgentKey,
    raw,
  } = listing || {};

  // Format address components using improved parsing
  const addressParts = getAddressParts(listing);
  const streetAddress = addressParts.streetAddress;
  const cityStateZip = addressParts.cityStateZip;
  
  // Format price with locale string
  const formattedPrice = price ? `$${price.toLocaleString()}` : 'Price unavailable';
  
  // Get neighborhood or subdivision, fallback to city if not available
  const neighborhood = (() => {
    if (subdivisionName && subdivisionName !== 'N/A' && subdivisionName.trim() !== '') {
      return subdivisionName;
    }
    return city || '';
  })();

  // Format Days on Market
  const formatDaysOnMarket = (days) => {
    if (days === null || days === undefined) return null;
    if (days === 0) return 'New';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  const isLandListing = (() => {
    const type = (propertyType || '').toString().toLowerCase();
    const subType = (propertySubType || '').toString().toLowerCase();
    return type.includes('land') || type.includes('lot') || subType.includes('land') || subType.includes('lot');
  })();

  const isCommercialListing = (() => {
    const type = (propertyType || '').toString().toLowerCase();
    return type.includes('commercial');
  })();

  const formatAcresParts = (acres) => {
    if (acres == null) return null;
    const n = Number(acres);
    if (!Number.isFinite(n) || n <= 0) return null;
    const rounded = Math.round(n * 100) / 100;
    const valueText = rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
    const labelText = rounded === 1 ? 'acre' : 'acres';
    return { valueText, labelText };
  };

  // Handle agent click to open contact modal
  const handleAgentClick = (e, memberKey) => {
    e.stopPropagation();
    if (!memberKey) return;
    
    setSelectedAgentKey(memberKey);
    setShowAgentModal(true);
    
    analyticsService.trackEvent({
      category: 'Contact',
      action: 'Open Agent Modal',
      label: memberKey,
      value: 1
    });
  };

  return (
    <>
      <div className="listing-card">
        {/* Card styles applied inline to maintain consistency */}
        <div style={{
          ...StyleConstants.propertyCard,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          maxWidth: '100%', // Prevent overflow on smaller screens
          cursor: 'pointer' // Add cursor pointer for entire card
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
        }}
        onMouseDown={(e) => {
          // Track if user starts selecting text
          if (!e.target.closest('[data-photo-section]') && !e.target.closest('[data-agent-link]')) {
            setIsSelectingText(false);
          }
        }}
        onMouseMove={(e) => {
          // If mouse moves while button is down, user is likely selecting text
          if (e.buttons === 1 && !e.target.closest('[data-photo-section]') && !e.target.closest('[data-agent-link]')) {
            setIsSelectingText(true);
          }
        }}
        onMouseUp={(e) => {
          // Check if user was selecting text or clicked on agent link
          if (!e.target.closest('[data-photo-section]') && !e.target.closest('[data-agent-link]')) {
            const selection = window.getSelection();
            const selectedText = selection.toString().trim();
            
            // If there's selected text or user was selecting, don't open modal
            if (selectedText.length > 0 || isSelectingText) {
              setIsSelectingText(false);
              return;
            }
            
            // Open modal if no text selection occurred
            setShowPhotoModal(true);
            
            // Track property card view with full property data
            analyticsService.trackEvent({
              category: 'Property',
              action: 'View Listing',
              label: address || streetAddress,
              value: 1,
              params: {
                property_id: listingId || listingKey,
                property_address: address || streetAddress,
                listing_agent: agentName || 'Unknown',
                property_price: price || 0,
                property_city: city || 'Unknown',
                property_neighborhood: subdivisionName || city || 'Unknown',
                bedrooms: beds || 0,
                bathrooms: baths || 0,
                square_feet: sqft || 0,
                days_on_market: daysOnMarket || 0
              }
            });
          }
        }}
        onClick={(e) => {
          // Prevent default click behavior since we're handling it in onMouseUp
          e.preventDefault();
        }}>
          {/* Property Image Section with consistent aspect ratio and photo count */} 
          <div 
            data-photo-section="true"
            style={{
              position: 'relative',
              paddingBottom: '65%', /* Maintain consistent aspect ratio */
              background: '#f0f0f0',
              overflow: 'hidden', // Ensures the image corners are rounded if image is larger than container
              cursor: 'pointer', // Added for clickability for modal
              WebkitTapHighlightColor: 'transparent' // Prevent tap highlight on this container
            }} 
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click from firing
              if (typeof onOpenModal === 'function') onOpenModal();
              setShowPhotoModal(true);
              
              // Track photo gallery open with full property data
              analyticsService.trackEvent({
                category: 'Property',
                action: 'Open Photo Gallery',
                label: address || streetAddress,
                value: photoCount,
                params: {
                  property_id: listingId || listingKey,
                  property_address: address || streetAddress,
                  listing_agent: agentName || 'Unknown',
                  property_price: price || 0,
                  property_city: city || 'Unknown',
                  property_neighborhood: subdivisionName || city || 'Unknown',
                  bedrooms: beds || 0,
                  bathrooms: baths || 0,
                  square_feet: sqft || 0
                }
              });
            }}>
            {/* Photo Count Badge - only show when image is loaded */}
            {photoCount > 0 && imageLoaded && (
              <div style={{
                position: 'absolute',
                bottom: '12px',
                right: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                zIndex: 1 // Ensure it's above the image
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="white" viewBox="0 0 16 16" style={{ marginRight: '4px' }}><path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2z"/><path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/></svg>
                {photoCount}
              </div>
            )}

            {/* Standard Status Badge - only show when image is loaded */}
            {imageLoaded && status && (
              <div style={{
                position: 'absolute',
                top: '12px',
                left: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                zIndex: 2,
                maxWidth: 'calc(100% - 80px)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {status}
              </div>
            )}

            {photo ? (
              <>
                {/* Loading placeholder */}
                {!imageLoaded && (
                  <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f0f0f0'
                  }} />
                )}
                
                {/* Actual image */}
                <img 
                  src={getGalleryImageUrl(photo)} 
                  alt={`${streetAddress}`}
                  loading={priority ? 'eager' : 'lazy'}
                  decoding="async"
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    top: 0,
                    left: 0,
                    cursor: 'pointer',
                    opacity: imageLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                />
              </>
            ) : (
              <div style={{ 
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#888',
                fontSize: '14px',
              }}>
                No image available
              </div>
            )}
          </div>
          
          {/* Property Details Section - Consistent height */}
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {/* Price and MLS # Row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center', // Vertically align price and MLS#
              marginBottom: '8px', // Keep overall bottom margin for the row
            }}>
              {/* Price */}
              <div style={{
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#222',
                lineHeight: '1.2',
              }}>
                {formattedPrice}
              </div>
              {/* MLS Number */}
              {listingId && ( // Render only if listingId exists
                <div style={{
                  fontSize: '12px', // Small text
                  color: '#555',    // Muted color for subtlety
                  marginLeft: '10px' // Space between price and MLS#
                }}>
                  MLS # {listingId}
                </div>
              )}
            </div>
            
            {/* Key Details - Consistent Height Section */}
            <div style={{ 
              display: 'flex', 
              marginBottom: '12px', 
              height: '24px', /* Match residential layout */
              alignItems: 'center'
            }}>
              {isLandListing || isCommercialListing ? (
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  color: '#444',
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {(() => {
                    const acres = formatAcresParts(lotSizeAcres);
                    if (!acres) return <span>--</span>;
                    return (
                      <>
                        <span style={{ fontWeight: 600 }}>{acres.valueText}</span>&nbsp;{acres.labelText}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  color: '#444',
                  lineHeight: '1.2',
                }}>
                  {beds != null && beds !== 0 && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{beds}</span> bed
                    </span>
                  )}
                  {beds != null && beds !== 0 && baths != null && (
                    <span style={{ margin: '0 4px', color: '#888' }}>•</span>
                  )}
                  {baths != null && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{baths}</span> bath
                    </span>
                  )}
                  {baths != null && sqft != null && sqft > 0 && (
                    <span style={{ margin: '0 4px', color: '#888' }}>•</span>
                  )}
                  {sqft != null && sqft > 0 && (
                    <span>
                      <span style={{ fontWeight: '600' }}>{sqft.toLocaleString()}</span> sqft
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Address - Fixed Height Regardless of Content */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '44px', /* Fixed height for address section */
              marginBottom: '8px',
              overflow: 'hidden',
            }}>
              <div style={{ 
                fontSize: '16px',
                fontWeight: '500',
                color: '#333',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {streetAddress}
              </div>
              <div style={{ 
                fontSize: '14px',
                color: '#666',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {cityStateZip}
              </div>
            </div>
            
            {/* Neighborhood/Subdivision */}
            {neighborhood && (
              <div style={{
                fontSize: '14px',
                color: '#555',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                height: '24px',
                lineHeight: '1.2',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '4px'}}>
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#555"/>
                </svg>
                <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{neighborhood}</span>
              </div>
            )}

            {/* Agent Name */}
            {agentName && (
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '0',
                display: 'flex',
                alignItems: 'center',
                height: '20px',
                lineHeight: '1.2',
              }}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg" 
                  style={{
                    marginRight: '4px',
                    cursor: listAgentKey ? 'pointer' : 'default',
                    transition: 'opacity 0.2s ease'
                  }}
                  data-agent-link
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAgentClick(e, listAgentKey);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseOver={(e) => {
                    if (listAgentKey) {
                      e.currentTarget.style.opacity = '0.7';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (listAgentKey) {
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                >
                  <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#666"/>
                </svg>
                <span 
                  data-agent-link
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAgentClick(e, listAgentKey);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    cursor: listAgentKey ? 'pointer' : 'default',
                    color: '#666',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (listAgentKey) {
                      e.currentTarget.style.color = '#0043ff';
                      e.currentTarget.style.textDecoration = 'underline';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (listAgentKey) {
                      e.currentTarget.style.color = '#666';
                      e.currentTarget.style.textDecoration = 'none';
                    }
                  }}
                >
                  {agentName}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Photo Modal */}
      {showPhotoModal && (
      <PhotoModalWithAgent
        listing={listing}
        onClose={() => setShowPhotoModal(false)}
        isMobile={isMobile}
      />
      )}

      {/* Agent Contact Modal */}
      {showAgentModal && (
        <AgentContactModal
          memberKey={selectedAgentKey}
          onClose={() => {
            setShowAgentModal(false);
            setSelectedAgentKey(null);
          }}
          show={showAgentModal}
          isMobile={isMobile}
        />
      )}
    </>
  );
});

export default ListingCard;

